import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { trackCTAClick } from '@/lib/analytics';

const trustSignals = [
  { icon: 'BoltIcon', text: 'Instant confirmation' },
  { icon: 'LockClosedIcon', text: 'Secure PayFast checkout' },
  { icon: 'ArrowPathIcon', text: 'Easy cancellation' },
  { icon: 'DevicePhoneMobileIcon', text: 'Mobile-friendly booking' },
];

export default function BookingCTA() {
  return (
    <section className="py-20 bg-card">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="relative rounded-3xl overflow-hidden gradient-primary">
          {/* Decorative circles */}
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 pointer-events-none" />
          <div className="absolute -bottom-20 -left-10 w-80 h-80 rounded-full bg-white/5 pointer-events-none" />
          <div className="absolute top-1/2 right-1/4 w-32 h-32 rounded-full bg-accent/10 pointer-events-none" />

          <div className="relative px-8 py-14 md:px-16 md:py-20 flex flex-col md:flex-row items-center gap-10">
            {/* Left copy */}
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-accent/20 border border-accent/30 rounded-full px-3 py-1.5 mb-5">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="text-accent text-xs font-semibold tracking-wide uppercase">Tee Times Available Today</span>
              </div>
              <h2 className="text-3xl md:text-4xl xl:text-5xl font-extrabold text-white leading-tight mb-4 text-balance">
                Your next round is<br />
                <span className="text-accent">one click away.</span>
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                Pick your date, choose your tee time, and pay securely online. No phone calls, no waiting — just golf.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Link
                  href="/online-booking"
                  onClick={() => trackCTAClick('Book Your Tee Time', 'booking_cta_section')}
                  className="btn-gold px-8 py-4 rounded-xl text-base font-bold inline-flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(212,175,55,0.35)]"
                >
                  <Icon name="CalendarDaysIcon" size={20} variant="outline" />
                  Book Your Tee Time
                </Link>
                <Link
                  href="/visitor-registration-sign-up-login"
                  className="px-8 py-4 rounded-xl text-base font-semibold text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200 inline-flex items-center justify-center gap-2"
                >
                  <Icon name="UserPlusIcon" size={20} variant="outline" />
                  Create Free Account
                </Link>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 justify-center md:justify-start">
                {trustSignals.map((s) => (
                  <div key={s.text} className="flex items-center gap-1.5">
                    <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={14} variant="outline" className="text-accent" />
                    <span className="text-white/60 text-xs font-medium">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: quick-stats card */}
            <div className="shrink-0 w-full md:w-72">
              <div className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-sm space-y-4">
                <div className="text-white/60 text-xs uppercase tracking-widest font-semibold mb-2">At a Glance</div>
                {[
                  { icon: 'TrophyIcon', label: '18-Hole Par 72', sub: 'Championship layout since 1886' },
                  { icon: 'ClockIcon', label: 'Open from 6:00 AM', sub: 'Seven days a week' },
                  { icon: 'MapPinIcon', label: 'New England Rd, PMB', sub: 'KwaZulu-Natal' },
                  { icon: 'CurrencyDollarIcon', label: 'From R 180', sub: 'Senior weekday rate' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={18} variant="outline" className="text-accent" />
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm leading-none">{item.label}</div>
                      <div className="text-white/50 text-xs mt-0.5">{item.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
